import { useRouter } from 'vue-router'
import { useToast } from 'vuestic-ui'

/**
 * Feature keys that can be locked based on subscription plan.
 * Extend this list as Nexacon adds more gated features.
 */
export type SubscriptionFeature =
  | 'advanced_analytics'
  | 'bulk_device_ops'
  | 'priority_support'
  | 'webhook_integration'
  | 'custom_branding'

/** Human-readable labels for upgrade prompts */
const FEATURE_LABELS: Record<SubscriptionFeature, string> = {
  advanced_analytics: 'Advanced Analytics',
  bulk_device_ops: 'Bulk Device Operations',
  priority_support: 'Priority Support',
  webhook_integration: 'Webhook Integration',
  custom_branding: 'Custom Branding',
}

export function useSubscription() {
  const router = useRouter()
  const { init: toast } = useToast()

  // ── Upgrade prompts ────────────────────────────────────────
  const showUpgradePrompt = (feature?: SubscriptionFeature) => {
    const label = feature ? FEATURE_LABELS[feature] : 'this feature'
    toast({
      message: `${label} requires a higher subscription plan. Please upgrade.`,
      color: 'warning',
    })
    // Navigate to a billing / subscription page if it exists
    try {
      router.push({ name: 'billing' })
    } catch {
      // route may not exist yet — silent fail
    }
  }

  /**
   * Simple feature check — currently returns true for all features
   * (Nexacon doesn't yet have per-plan gating).
   * Replace the body with real plan logic once the backend is ready.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const hasFeature = (_feature?: string): boolean => {
    return true
  }

  const requireFeature = (feature: SubscriptionFeature): boolean => {
    if (!hasFeature(feature)) {
      showUpgradePrompt(feature)
      return false
    }
    return true
  }

  const handleApiError = (error: any): boolean => {
    if (error?.response?.status === 403) {
      const errorMessage = error.response?.data?.detail || 'Access denied'
      if (errorMessage.includes('subscription') || errorMessage.includes('expired')) {
        showUpgradePrompt()
      } else {
        toast({ message: errorMessage, color: 'danger' })
      }
      return true
    }
    return false
  }

  return {
    hasFeature,
    requireFeature,
    showUpgradePrompt,
    handleApiError,
  }
}
